import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button } from 'rebass';
import { Label, Input, Select } from '@rebass/forms';
import { Link } from 'react-router-dom';
import moment from 'moment';

import helpers from 'utils/helpers';
import serviceIntegrations from 'services/integrations';
import { useIncentiveProvidersQuery } from 'api/useIncentiveProvidersQuery';
import Portal from 'components/Portal';
import NiceModal from 'components/NiceModal';
import useForm from 'hooks/useForm';
import {
    CURRENCY_USD,
    INCENTIVE_PAYMENT_METHOD,
    INCENTIVE_PAYMENT_METHOD_LABEL,
    CURRENCY_SYMBOL
} from 'utils/constants';
import { DATE_FORMATS } from 'utils/dateFormats';

/**
 * Payment methods that exist independently of the provider manifest. On master,
 * mark-as-paid and the first-party balance never depended on any Tremendous state,
 * so they must stay submittable even while the manifest request is in flight or
 * has failed.
 */
const MANIFEST_INDEPENDENT_PAYMENT_METHODS = [
    INCENTIVE_PAYMENT_METHOD.MARK_AS_PAID,
    INCENTIVE_PAYMENT_METHOD.FIRST_PARTY,
    INCENTIVE_PAYMENT_METHOD.FIRST_PARTY_VIA_TREMENDOUS
];

/**
 * The funding-account check before it has been asked, or for a provider that has no
 * such concept.
 *
 * `provider` and `studyId` record what the check was made for, so a settled result is
 * never read as if it spoke for a different selection.
 */
const NO_FUNDING_ACCOUNT_CHECK = {
    provider: null,
    studyId: null,
    loading: false,
    account: null,
    reason: null,
    failed: false
};

/**
 * Modal for making payments: the dscout-funded first-party balance, any incentive
 * provider the customer has connected, and Mark as Paid.
 *
 * The provider options are rendered from the manifest rather than listed here, so
 * connecting a new provider makes it payable with no change to this component.
 *
 * For a provider whose manifest sets funding_account_per_study, the modal asks the API
 * which provider-side account this study's payout will draw on and shows it (with its
 * balance) before the researcher confirms — the same answer the payout itself uses.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen Modal open state
 * @param {() => void} props.onClose Close modal callback
 * @param {string} props.title Modal title
 * @param {(data: { amount: number, paymentType: string, date: string }) => Promise<void>} props.onPay Payment callback
 * @param {Object} props.auth Auth object
 * @param {Object} props.study Study object
 * @param {Array<Object>?} props.incentiveProviders Provider manifest from useIncentiveProvidersQuery
 * @param {boolean?} props.providersLoading Manifest request in flight
 * @param {boolean?} props.providersFailed Manifest request failed
 * @param {Array?} props.payees People (Person) to be paid
 * @param {{ amount?: number, paymentType?: string, paymentDate?: string }} props.defaultValues Default form values
 * @param {(studyId: number, provider: string) => Promise<Object>?} props.fundingAccountLookup Resolves the funding account a study pays from; defaults to the API call
 */
export function PaymentModal({
    isOpen,
    onClose,
    title,
    onPay,
    auth,
    study,
    incentiveProviders,
    providersLoading,
    providersFailed,
    payees,
    defaultValues,
    fundingAccountLookup = serviceIntegrations.getStudyIncentiveFundingAccount
}) {
    const { register, handleSubmit, watch } = useForm({
        initialForm: {
            amount: defaultValues.amount || 0,
            paymentType: defaultValues.paymentType || '',
            paymentDate: moment(defaultValues.paymentDate).format(DATE_FORMATS.INPUT_DATE_TIME_LOCAL)
        }
    });
    const firstPartyEnabled = useMemo(() => helpers.isFirstPartyIncentivesEnabled(auth), [auth]);
    const firstPartyViaProvider = useMemo(() => helpers.isFirstPartyViaProviderEnabled(auth), [auth]);
    const payableProviders = useMemo(() => helpers.payableIncentiveProviders(auth, incentiveProviders), [
        auth,
        incentiveProviders
    ]);
    const [isProcessing, setIsProcessing] = useState(false);
    const paymentType = watch('paymentType');
    const amount = watch('amount');

    // The selected method, when it is one of the customer's own connected providers.
    // For that rail the payment method IS the provider name.
    const selectedProvider = useMemo(() => payableProviders.find(provider => provider.name === paymentType) || null, [
        payableProviders,
        paymentType
    ]);
    const missingTarget = useMemo(
        () => !!selectedProvider && !helpers.getStudyIncentiveTarget(study, selectedProvider.name),
        [selectedProvider, study]
    );
    const unsupportedCurrency = useMemo(
        () => !!selectedProvider && !helpers.providerSupportsStudyCurrency(selectedProvider, study),
        [selectedProvider, study]
    );

    // Gated on isOpen, not just the selection: two of the three call sites keep the
    // modal mounted and only toggle isOpen, and useForm holds its values in a ref that
    // close does not reset — so without this the balance shown on a reopen is the one
    // read before the previous payout, and a check that failed once stays failed.
    const checksFundingAccount = !!isOpen && !!selectedProvider?.funding_account_per_study && !!study?.id;
    const [fundingAccountCheck, setFundingAccountCheck] = useState(NO_FUNDING_ACCOUNT_CHECK);

    useEffect(() => {
        if (!checksFundingAccount) {
            setFundingAccountCheck(NO_FUNDING_ACCOUNT_CHECK);
            return undefined;
        }

        // Guards against a slow answer for a provider the researcher has since switched
        // away from landing on the one now selected.
        let current = true;
        const checkedFor = { provider: selectedProvider.name, studyId: study.id };
        setFundingAccountCheck({ ...NO_FUNDING_ACCOUNT_CHECK, ...checkedFor, loading: true });

        fundingAccountLookup(study.id, selectedProvider.name)
            .then(result => {
                if (!current) return;
                setFundingAccountCheck({
                    ...checkedFor,
                    loading: false,
                    account: result?.funding_account || null,
                    reason: result?.reason || null,
                    failed: false
                });
            })
            .catch(() => {
                if (!current) return;
                setFundingAccountCheck({ ...NO_FUNDING_ACCOUNT_CHECK, ...checkedFor, failed: true });
            });

        return () => {
            current = false;
        };
        // The provider's name and the study id are what decide the answer; the lookup
        // function is a stable prop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checksFundingAccount, selectedProvider?.name, study?.id]);

    /*
        The lookup runs in a passive effect, so on the render right after a provider
        change the state still holds the *previous* provider's settled answer. Reading
        it would name that provider's account and, for one frame, enable Pay for a
        selection whose budget has not been checked at all. A check that does not carry
        the current selection therefore reads as "still checking" — which is the truth,
        and which every consumer below already handles.
    */
    const currentFundingAccountCheck =
        fundingAccountCheck.provider === selectedProvider?.name && fundingAccountCheck.studyId === study?.id
            ? fundingAccountCheck
            : { ...NO_FUNDING_ACCOUNT_CHECK, loading: true };

    // A provider-rail payment cannot be confirmed until the account it will draw on is
    // known: unresolved (nothing chosen, nothing to fall back on) or unknown (the check
    // failed) both block, so the researcher never pays from an account they never saw.
    const fundingAccountBlocks =
        checksFundingAccount &&
        (currentFundingAccountCheck.loading ||
            currentFundingAccountCheck.failed ||
            !!currentFundingAccountCheck.reason ||
            !currentFundingAccountCheck.account);

    const isDisabled = useMemo(() => {
        // While the manifest is in flight or failed, the connected providers are not
        // known, so a provider-rail selection cannot be validated. Only that rail is
        // blocked: mark-as-paid and the first-party balance do not depend on the
        // manifest and submit exactly as they did before it existed.
        if ((providersLoading || providersFailed) && !MANIFEST_INDEPENDENT_PAYMENT_METHODS.includes(paymentType)) {
            return true;
        }

        // if type or amount is empty
        if (!paymentType || !amount || amount <= 0) return true;

        // Anything that is not one of the manifest-independent methods can only be a
        // provider's machine name, so it has to resolve to a provider the customer has
        // connected. A type that does not — a default carried in from the caller, or a
        // provider that left the manifest while the modal was open — has no reward, no
        // currency and no funding account to validate against, and the API would refuse
        // it anyway. Fail closed rather than post it.
        if (!MANIFEST_INDEPENDENT_PAYMENT_METHODS.includes(paymentType) && !selectedProvider) return true;

        // if a provider is selected but the study has no reward picked for it
        if (missingTarget) return true;

        // if a provider is selected that can't pay in the study's currency
        if (unsupportedCurrency) return true;

        // if a provider is selected whose funding account is not settled yet
        if (fundingAccountBlocks) return true;

        // if First Party is selected but the study currency is not USD
        if (paymentType === INCENTIVE_PAYMENT_METHOD.FIRST_PARTY && study.incentive_currency !== CURRENCY_USD)
            return true;

        return false;
    }, [
        paymentType,
        amount,
        study,
        selectedProvider,
        missingTarget,
        unsupportedCurrency,
        fundingAccountBlocks,
        providersLoading,
        providersFailed
    ]);

    const onSubmit = handleSubmit(async data => {
        setIsProcessing(true);
        await onPay(data);
        setIsProcessing(false);
    });

    return (
        <Portal>
            <NiceModal
                isOpen={isOpen}
                shouldCloseOnOverlayClick
                onRequestClose={onClose}
                style={{ content: { width: '420px' } }}
                title={title}
            >
                <form onSubmit={onSubmit}>
                    <Box>
                        {payees && (
                            <Box mb={3}>
                                <Label>To be paid ({payees.length} total)</Label>
                                <Box>
                                    {payees.map(payee => (
                                        <Box key={payee.id} as="p">
                                            {helpers.personGetFullName(payee)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Label>Payment method</Label>
                        <Select
                            {...register('paymentType')}
                            required
                            aria-describedby={missingTarget ? 'payment-method-error' : undefined}
                        >
                            <option value=""></option>
                            {firstPartyEnabled && (
                                <option
                                    value={
                                        firstPartyViaProvider
                                            ? INCENTIVE_PAYMENT_METHOD.FIRST_PARTY_VIA_TREMENDOUS
                                            : INCENTIVE_PAYMENT_METHOD.FIRST_PARTY
                                    }
                                >
                                    {firstPartyViaProvider
                                        ? INCENTIVE_PAYMENT_METHOD_LABEL.FIRST_PARTY_VIA_TREMENDOUS
                                        : INCENTIVE_PAYMENT_METHOD_LABEL.FIRST_PARTY}
                                </option>
                            )}
                            {payableProviders.map(provider => (
                                <option key={provider.name} value={provider.name}>
                                    {provider.label}
                                </option>
                            ))}
                            <option value={INCENTIVE_PAYMENT_METHOD.MARK_AS_PAID}>
                                {INCENTIVE_PAYMENT_METHOD_LABEL.MARK_AS_PAID}
                            </option>
                        </Select>
                        {providersLoading && <small>Loading your connected payment methods…</small>}
                        {providersFailed && (
                            <Box role="alert" mt={3} p={3} className="error border">
                                We could not load your connected payment methods, so this list is incomplete. Please
                                reload the page and try again.
                            </Box>
                        )}
                        {missingTarget && (
                            <Box role="alert" id="payment-method-error" mt={3} p={3} className="error border">
                                Missing {selectedProvider.label} {selectedProvider.target_label.toLowerCase()} - please
                                update in <Link to={`/studies/${study.id}/settings`}>Study Settings</Link>.
                            </Box>
                        )}
                        {/*
                            Inside the live region rather than given the select's
                            aria-describedby id: a missing reward and an unsupported
                            currency can both be true at once, and two nodes sharing
                            one id means only the first is read. This is the third
                            blocking condition on Pay, so it is announced like the
                            other two instead of being drawn silently.
                        */}
                        <Box role="status" aria-live="polite">
                            {unsupportedCurrency && (
                                <small className="warning">
                                    This payment method supports {selectedProvider.supported_currencies.join(', ')}{' '}
                                    only.
                                </small>
                            )}
                        </Box>
                        {/*
                            Mounted unconditionally: these four messages appear and
                            disappear purely from async state, and a live region added
                            to the DOM at the same moment as its content is often not
                            announced. Without it a screen-reader user picks a provider,
                            hears nothing, and finds Pay disabled with no stated cause.
                        */}
                        <Box role="status" aria-live="polite">
                            {checksFundingAccount && currentFundingAccountCheck.loading && (
                                <small>Checking your {selectedProvider.label} funding account…</small>
                            )}
                            {checksFundingAccount && currentFundingAccountCheck.account && (
                                <Box mt={2} className="fs-body-14 color-text-secondary">
                                    Paying from <strong>{currentFundingAccountCheck.account.name}</strong>
                                    {currentFundingAccountCheck.account.balance !== null &&
                                        currentFundingAccountCheck.account.balance !== undefined && (
                                            <>
                                                {' · Balance '}
                                                {/*
                                                    CURRENCY_SYMBOL only maps the currencies a study can pay
                                                    in; this balance is the provider-side account's, which is
                                                    not constrained by that list. A platform holding CAD would
                                                    have read "$480.00" on the line the researcher checks right
                                                    before confirming, so an unmapped code is printed as itself.
                                                */}
                                                {CURRENCY_SYMBOL[currentFundingAccountCheck.account.currency_code] ||
                                                    `${currentFundingAccountCheck.account.currency_code} `}
                                                {currentFundingAccountCheck.account.balance}
                                            </>
                                        )}
                                </Box>
                            )}
                            {checksFundingAccount &&
                                !currentFundingAccountCheck.loading &&
                                currentFundingAccountCheck.reason && (
                                    <Box mt={3} p={3} className="error border">
                                        {currentFundingAccountCheck.reason} Please update in{' '}
                                        <Link to={`/studies/${study.id}/settings`}>Study Settings</Link>.
                                    </Box>
                                )}
                            {checksFundingAccount && currentFundingAccountCheck.failed && (
                                <Box mt={3} p={3} className="error border">
                                    We could not check your {selectedProvider.label} funding account. Please try again.
                                </Box>
                            )}
                        </Box>
                        {paymentType === INCENTIVE_PAYMENT_METHOD.FIRST_PARTY &&
                            study.incentive_currency !== CURRENCY_USD && (
                                <small className="warning">This payment method supports USD only.</small>
                            )}
                        <Label mt={3}>
                            Amount ({CURRENCY_SYMBOL[study.incentive_currency]} {study.incentive_currency})
                        </Label>
                        <Input
                            {...register('amount', { formatter: parseInt })}
                            type="number"
                            step="1"
                            min="0"
                            placeholder="e.g. 50"
                            required
                        />
                        {paymentType === INCENTIVE_PAYMENT_METHOD.MARK_AS_PAID && (
                            <>
                                <Label mt={3}>Payment date</Label>
                                <Input
                                    {...register('paymentDate')}
                                    type="datetime-local"
                                    required
                                    style={{ width: '100%' }}
                                />
                            </>
                        )}
                    </Box>
                    <Box className="modal-actions">
                        <Button type="button" variant="secondary-gray" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            sx={{ marginRight: 0 }}
                            disabled={isProcessing || isDisabled}
                        >
                            {isProcessing
                                ? 'Processing...'
                                : /* Anything other than mark-as-paid actually moves money, so it
                                     confirms the amount. Stated as "not mark as paid" rather than a
                                     list of methods, so a newly connected provider is covered. */
                                paymentType && paymentType !== INCENTIVE_PAYMENT_METHOD.MARK_AS_PAID
                                ? `Confirm, pay ${CURRENCY_SYMBOL[study.incentive_currency]}${(
                                      amount * payees.length
                                  ).toFixed(0)}`
                                : INCENTIVE_PAYMENT_METHOD_LABEL.MARK_AS_PAID_BUTTON}
                        </Button>
                    </Box>
                </form>
            </NiceModal>
        </Portal>
    );
}

/**
 * PaymentModal with the incentive provider manifest supplied for it.
 *
 * The modal is still rendered from class components, which can't call hooks, so the
 * fetch lives here rather than being threaded through as a prop from three
 * different places. The named PaymentModal export stays pure and is what tests
 * render. React Query dedupes the request across simultaneous mounts.
 *
 * @param {Object} props Same props as PaymentModal; an explicit incentiveProviders wins
 */
export function PaymentModalWithProviders(props) {
    // Two of the three call sites mount the modal unconditionally and only toggle
    // isOpen, and the shared QueryClient is built with no defaults (staleTime 0,
    // refetchOnWindowFocus on) — so without this the manifest is fetched on every
    // study page load and again on every window refocus, for a modal most of those
    // visits never open. While disabled the query reports loading, which keeps the
    // "loading your payment methods" line showing for the real fetch on open.
    const { data: incentiveProviders, isLoading, isError } = useIncentiveProvidersQuery({
        enabled: !!props.isOpen
    });

    // An explicit manifest wins over the query outright — its loading and error state
    // included. Passing the query's flags through anyway would let an unrelated
    // in-flight or failed request disable provider payments for a caller that already
    // has the manifest it needs.
    const hasExplicitProviders = !!props.incentiveProviders;

    return (
        <PaymentModal
            {...props}
            incentiveProviders={props.incentiveProviders || incentiveProviders}
            providersLoading={isLoading && !hasExplicitProviders}
            providersFailed={isError && !hasExplicitProviders}
        />
    );
}

export default PaymentModalWithProviders;
